// Client-side validation for tenant `CustomProviderRule` lists (O-697).
//
// Mirrors the server-side `validate_custom_provider_rules` in
// `backend/crates/api-server/src/grpc/tenant_configuration.rs` and
// `shared_protos::provider::AiProvider::is_valid`. The server is still
// the source of truth; these checks exist so the settings UI can give
// fast feedback before a PUT. Limits are inlined here as constants —
// keep them in lockstep with the server-side `MAX_*_PER_*` constants.
//
// Ported into the Origin dashboard under src/lib/ during the O-1162
// dashboard migration (was dashboard-legacy/src/core/customProviders.js);
// this closes the O-1164 parity gap.
//
// In-memory / wire shape (matches the proto JSON the grpc-gateway emits):
//   {
//     id: string,
//     provider_tag: string,
//     domain_patterns: [{ exact|wildcardSuffix|wildcardPrefix: string }],
//     path_patterns:   [{ contains: string }],
//     method:   "CUSTOM_PROVIDER_HTTP_METHOD_*",
//     strategy: "CUSTOM_CAPTURE_STRATEGY_*",
//   }
//
// The grpc-gateway v2 default protojson marshaler emits oneof fields
// flat (no `kind` wrapper) and uses lowerCamelCase field names. The
// proto declares them snake_case (`wildcard_suffix`) but the wire form
// the dashboard sees on GET is camelCase (`wildcardSuffix`). The editor
// produces the same flat shape on PUT for symmetry.
//
// The readers below are backward-compatible with the old `kind`-wrapped
// format so that any in-memory cached state or browser localStorage
// round-trips gracefully. New data is always written in the flat format.
//
// `BUILTIN_PROVIDER_TAGS` is the third mirror of
// `shared_protos::provider::BUILTIN_PROVIDERS` (Rust) and
// `skwach/skwach/skwach/proto.py::PROVIDER_ENUM_TAGS` (Python). When
// adding a new built-in provider, update all three in the same PR — see
// `backend/AGENTS.md` "Provider tags and AI provider attribution". The
// parity test in customProviders.test.js fails if this set drifts.

export const BUILTIN_PROVIDER_TAGS = new Set([
  "anthropic",
  "openai",
  "google",
  "microsoft",
  "cursor",
  "deepseek",
  "groq",
  "mistral",
  "openrouter",
  "perplexity",
  "together-ai",
  "huggingface",
  "xai",
  "local",
]);

// Tenant tags are lower-kebab, 1-64 chars, must start with a letter,
// no leading or trailing hyphen. Matches `AiProvider::is_valid`.
export const CUSTOM_PROVIDER_TAG_RE = /^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/;

// Bare DNS-name shape: ≥2 labels, ASCII alphanumeric or hyphen, no
// leading/trailing hyphen on any label. Used for `Exact` and
// `WildcardSuffix` variant values. Wildcard prefixes (e.g.
// `-aiplatform.googleapis.com`) get a looser check that allows a
// leading hyphen on the first label.
export const DOMAIN_PATTERN_RE =
  /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;

export const WILDCARD_PREFIX_RE =
  /^(-?[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;

export const MAX_TENANT_RULES_PER_TENANT = 256;
export const MAX_DOMAIN_PATTERNS_PER_RULE = 32;
export const MAX_PATH_PATTERNS_PER_RULE = 32;
export const MAX_PATH_PATTERN_LEN = 256;

const CONTROL_CHAR_RE = /[\x00-\x1f\x7f]/;

// Allowed proto JSON enum strings for `method`. Mirror of the Rust
// `CustomProviderHttpMethod` enum. Used by the form's <select>.
export const ALLOWED_HTTP_METHODS = Object.freeze([
  "CUSTOM_PROVIDER_HTTP_METHOD_UNSPECIFIED",
  "CUSTOM_PROVIDER_HTTP_METHOD_GET",
  "CUSTOM_PROVIDER_HTTP_METHOD_POST",
  "CUSTOM_PROVIDER_HTTP_METHOD_PUT",
  "CUSTOM_PROVIDER_HTTP_METHOD_DELETE",
  "CUSTOM_PROVIDER_HTTP_METHOD_PATCH",
]);

const ALLOWED_HTTP_METHODS_SET = new Set(ALLOWED_HTTP_METHODS);

// Render-ready options for the form's HTTP-method <select>.
export const ALLOWED_HTTP_METHOD_OPTIONS = Object.freeze([
  { value: "CUSTOM_PROVIDER_HTTP_METHOD_UNSPECIFIED", label: "Any" },
  { value: "CUSTOM_PROVIDER_HTTP_METHOD_GET", label: "GET" },
  { value: "CUSTOM_PROVIDER_HTTP_METHOD_POST", label: "POST" },
  { value: "CUSTOM_PROVIDER_HTTP_METHOD_PUT", label: "PUT" },
  { value: "CUSTOM_PROVIDER_HTTP_METHOD_DELETE", label: "DELETE" },
  { value: "CUSTOM_PROVIDER_HTTP_METHOD_PATCH", label: "PATCH" },
]);

// Tenant custom-provider capture-strategy enum (O-697). The proto JSON
// value strings are what the gateway accepts on PUT; pair each with the
// human-readable label the settings UI renders in the strategy dropdown.
// Source of truth: `CustomCaptureStrategy` in
// `shared-protos/proto/endpoint/v1/endpoint.proto`.
export const CustomCaptureStrategy = Object.freeze({
  UNSPECIFIED: "CUSTOM_CAPTURE_STRATEGY_UNSPECIFIED",
  FULL_BODY: "CUSTOM_CAPTURE_STRATEGY_FULL_BODY",
  FILE_UPLOAD: "CUSTOM_CAPTURE_STRATEGY_FILE_UPLOAD",
  WEBSOCKET: "CUSTOM_CAPTURE_STRATEGY_WEBSOCKET",
});

export const CUSTOM_CAPTURE_STRATEGY_OPTIONS = Object.freeze([
  { value: CustomCaptureStrategy.FULL_BODY, label: "Full request body (default)" },
  { value: CustomCaptureStrategy.FILE_UPLOAD, label: "File upload" },
  { value: CustomCaptureStrategy.WEBSOCKET, label: "WebSocket frames" },
]);

// Allowed `kind` variants for a `CustomProviderDomainPattern`. Pinned in
// the order the form's variant <select> renders them.
export const DOMAIN_PATTERN_VARIANTS = Object.freeze([
  {
    value: "exact",
    label: "Exact hostname",
    help: "Match a single hostname exactly (e.g. api.acme.example).",
  },
  {
    value: "wildcardSuffix",
    label: "Subdomain wildcard",
    help: "Match any subdomain of the bare domain (e.g. acme.example matches api.acme.example but not acme.example).",
  },
  {
    value: "wildcardPrefix",
    label: "Literal suffix wildcard",
    help: "Match any hostname ending with the literal suffix (e.g. -aiplatform.googleapis.com matches us-central1-aiplatform.googleapis.com).",
  },
]);

const DOMAIN_VARIANT_KEYS = new Set(DOMAIN_PATTERN_VARIANTS.map((v) => v.value));

// Mapping from camelCase proto JSON field names (what the gateway emits)
// to the internal UI variant identifiers.
const DOMAIN_VARIANT_KEY_MAP = Object.freeze({
  exact: "exact",
  wildcardSuffix: "wildcardSuffix",
  wildcardPrefix: "wildcardPrefix",
});

// Extract the single variant key + value from a domain pattern.
// Accepts both the flat proto JSON format and the legacy `kind`-wrapped
// format for backward compatibility. Returns `null` if the pattern is
// malformed (no variant, multi-key, non-string value, unknown variant).
function readDomainPattern(pattern) {
  if (!pattern || typeof pattern !== "object") return null;

  // Backward-compatible: accept the old `kind`-wrapped format.
  const source = pattern.kind && typeof pattern.kind === "object"
    ? pattern.kind
    : pattern;

  const keys = Object.keys(source).filter((k) => k !== "kind");
  if (keys.length !== 1) return null;
  const protoKey = keys[0];
  const value = source[protoKey];
  if (typeof value !== "string") return null;
  const variant = DOMAIN_VARIANT_KEY_MAP[protoKey];
  if (!variant || !DOMAIN_VARIANT_KEYS.has(variant)) return null;
  return { variant, value };
}

function readPathPattern(pattern) {
  if (!pattern || typeof pattern !== "object") return null;

  // Backward-compatible: accept the old `kind`-wrapped format.
  const source = pattern.kind && typeof pattern.kind === "object"
    ? pattern.kind
    : pattern;

  const keys = Object.keys(source).filter((k) => k !== "kind");
  if (keys.length !== 1 || keys[0] !== "contains") return null;
  const value = source.contains;
  if (typeof value !== "string") return null;
  return { variant: "contains", value };
}

/**
 * Validate a list of `CustomProviderRule` objects in dashboard shape
 * (typed `domain_patterns` / `path_patterns` matching the proto JSON
 * wire format — see module header).
 *
 * Returns an array of error objects, one per input rule, with keys
 * matching the offending field (`id`, `provider_tag`, `domain_patterns`,
 * `path_patterns`, `method`). An empty object at index `i` means rule
 * `i` is valid.
 *
 * The list-level cap (`MAX_TENANT_RULES_PER_TENANT`) is intentionally
 * *not* reported per-rule — callers should check `rules.length` against
 * the constant and surface that condition at the list level.
 */
export function validateCustomProviderRules(rules) {
  if (!Array.isArray(rules)) return [];
  const errs = rules.map(() => ({}));
  const seenIds = new Set();
  rules.forEach((r, i) => {
    const id = (r?.id || "").trim();
    if (!id) {
      errs[i].id = "id is required";
    } else if (seenIds.has(id)) {
      errs[i].id = "duplicate id";
    } else {
      seenIds.add(id);
    }

    const tag = (r?.provider_tag || "").trim();
    if (!tag) {
      errs[i].provider_tag = "provider_tag is required";
    } else if (!CUSTOM_PROVIDER_TAG_RE.test(tag) || tag.length > 64) {
      errs[i].provider_tag =
        "lower-kebab, 1-64 chars, must start with a letter, no trailing hyphen";
    } else if (BUILTIN_PROVIDER_TAGS.has(tag)) {
      errs[i].provider_tag = `'${tag}' is a built-in provider. Choose a different tag.`;
    }

    const domains = Array.isArray(r?.domain_patterns) ? r.domain_patterns : [];
    if (domains.length === 0) {
      errs[i].domain_patterns = "at least one domain_pattern is required";
    } else if (domains.length > MAX_DOMAIN_PATTERNS_PER_RULE) {
      errs[i].domain_patterns = `max ${MAX_DOMAIN_PATTERNS_PER_RULE} domain_patterns per rule`;
    } else {
      for (const d of domains) {
        const parsed = readDomainPattern(d);
        if (!parsed) {
          errs[i].domain_patterns =
            "each entry must have exactly one of exact / wildcardSuffix / wildcardPrefix";
          break;
        }
        const re =
          parsed.variant === "wildcardPrefix" ? WILDCARD_PREFIX_RE : DOMAIN_PATTERN_RE;
        if (!parsed.value || !re.test(parsed.value)) {
          errs[i].domain_patterns = `'${parsed.value}' is not a valid DNS name`;
          break;
        }
      }
    }

    const paths = Array.isArray(r?.path_patterns) ? r.path_patterns : [];
    if (paths.length === 0) {
      errs[i].path_patterns = "at least one path_patterns entry is required";
    } else if (paths.length > MAX_PATH_PATTERNS_PER_RULE) {
      errs[i].path_patterns = `max ${MAX_PATH_PATTERNS_PER_RULE} path_patterns entries per rule`;
    } else {
      for (const p of paths) {
        const parsed = readPathPattern(p);
        if (!parsed) {
          errs[i].path_patterns = "each entry must have contains as a non-empty string";
          break;
        }
        if (
          parsed.value.length === 0 ||
          parsed.value.length > MAX_PATH_PATTERN_LEN ||
          CONTROL_CHAR_RE.test(parsed.value)
        ) {
          errs[i].path_patterns = `each path_patterns entry must be 1-${MAX_PATH_PATTERN_LEN} chars, no control chars`;
          break;
        }
      }
    }

    const method = r?.method;
    if (method && !ALLOWED_HTTP_METHODS_SET.has(method)) {
      errs[i].method = `method '${method}' is not a recognised CustomProviderHttpMethod value`;
    }
  });
  return errs;
}

/**
 * Returns `true` iff the rule list has any per-rule validation errors or
 * exceeds the tenant-wide rule cap.
 */
export function hasAnyValidationError(errs, ruleCount) {
  return (
    errs.some((e) => Object.keys(e).length > 0) ||
    ruleCount > MAX_TENANT_RULES_PER_TENANT
  );
}

// Convenience constructors for the editor — kept here so a future proto
// rename only needs to be updated in one place.
export function newDomainPattern(variant, value = "") {
  if (!DOMAIN_VARIANT_KEYS.has(variant)) {
    throw new Error(`unknown domain pattern variant: ${variant}`);
  }
  return { [variant]: value };
}

export function newPathPattern(value = "") {
  return { contains: value };
}

export function readDomainPatternEntry(pattern) {
  return readDomainPattern(pattern);
}
